// Blog data configuration and metadata
// This contains blog post metadata, structured data helpers, and blog configuration
//
// 📝 IMPORTANT: When adding new blog posts, remember to update the landing page:
// Run: node scripts/update-latest-blog-posts.js
// Or manually update: src/data/latestBlogPosts.js

// Centralized blog post dates configuration
export const blogDates = {
  'html-form-backend-setup-guide': {
    publishDate: 'June 15, 2025',
    datePublished: '2025-06-15',
    dateModified: '2025-06-15'
  },
  'contact-form-security-best-practices': {
    publishDate: 'June 15, 2025',
    datePublished: '2025-06-15',
    dateModified: '2025-06-15'
  },
  'jamstack-forms-complete-guide': {
    publishDate: 'June 20, 2025',
    datePublished: '2025-06-20',
    dateModified: '2025-06-20'
  },
  'adding-contact-forms-static-sites': {
    publishDate: 'June 15, 2025',
    datePublished: '2025-06-15',
    dateModified: '2025-06-15'
  },
  'static-site-forms-solutions-comparison': {
    publishDate: 'June 15, 2025',
    datePublished: '2025-06-15',
    dateModified: '2025-06-15'
  },
  'formcare-vs-building-your-own-backend': {
    publishDate: 'June 15, 2025',
    datePublished: '2025-06-15',
    dateModified: '2025-06-15'
  },
  'preventing-form-spam-bot-attacks': {
    publishDate: 'June 20, 2025',
    datePublished: '2025-06-20',
    dateModified: '2025-06-20'
  },
  'contact-form-gdpr-compliance-guide': {
    publishDate: 'June 20, 2025',
    datePublished: '2025-06-20',
    dateModified: '2025-06-20'
  },
  'form-validation-best-practices': {
    publishDate: 'June 20, 2025',
    datePublished: '2025-06-20',
    dateModified: '2025-06-20'
  },
  'connecting-flux-ui-to-formcare-guide': {
    publishDate: 'June 25, 2025',
    datePublished: '2025-06-25',
    dateModified: '2025-06-25'
  },
  'nextjs-contact-form-without-firebase': {
    publishDate: 'June 25, 2025',
    datePublished: '2025-06-25',
    dateModified: '2025-06-25'
  },
  'react-form-submissions-no-server': {
    publishDate: 'June 25, 2025',
    datePublished: '2025-06-25',
    dateModified: '2025-06-25'
  },
  'aws-lambda-form-endpoint-alternative': {
    publishDate: 'June 25, 2025',
    datePublished: '2025-06-25',
    dateModified: '2025-06-25'
  },
  'formcare-data-privacy-security': {
    publishDate: 'June 30, 2025',
    datePublished: '2025-06-30',
    dateModified: '2025-06-30'
  },
  'webflow-file-uploads-guide': {
    publishDate: 'June 30, 2025',
    datePublished: '2025-06-30',
    dateModified: '2025-06-30'
  },
  'formcare-customer-support-service': {
    publishDate: 'September 21, 2025',
    datePublished: '2025-09-21',
    dateModified: '2025-09-21'
  },
  'custom-notification-email-form-management': {
    publishDate: 'September 28, 2025',
    datePublished: '2025-09-28',
    dateModified: '2025-09-28'
  },
  'formcare-design-update': {
    publishDate: 'May 10, 2026',
    datePublished: '2026-05-10',
    dateModified: '2026-05-10'
  },
  'wix-formcare-integration-guide': {
    publishDate: 'May 30, 2026',
    datePublished: '2026-05-30',
    dateModified: '2026-05-30'
  },
  'chatgpt-cloudflare-landing-page-guide': {
    publishDate: 'May 31, 2026',
    datePublished: '2026-05-31',
    dateModified: '2026-05-31'
  },
  'formcare-antispam-form-protection': {
    publishDate: 'August 2, 2026',
    datePublished: '2026-08-02',
    dateModified: '2026-08-02'
  },
  'formcare-product-update-simpler-signup': {
    publishDate: 'September 25, 2026',
    datePublished: '2026-09-25',
    dateModified: '2026-09-25'
  }
};

export const createStructuredData = (title, description, author, publishDate, image, url) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": title,
  "description": description,
  "author": {
    "@type": "Organization",
    "name": author
  },
  "publisher": {
    "@type": "Organization",
    "name": "FormCare",
    "logo": {
      "@type": "ImageObject",
      "url": "https://formcare.io/logo.png"
    }
  },
  "datePublished": publishDate,
  "dateModified": publishDate,
  "image": `https://formcare.io${image}`,
  "url": `https://formcare.io${url}`
});

export const blogPostMetadata = {
  'html-form-backend-setup-guide': {
    title: "How to Set Up HTML Form Backend in 2 Minutes | FormCare",
    description: "Learn how to quickly set up a backend for your HTML forms without writing any server-side code. Perfect for static sites and JAMstack applications.",
    keywords: "HTML form backend, static site forms, JAMstack forms, form endpoint, contact form backend, form submission handler",
    ogTitle: "How to Set Up HTML Form Backend in 2 Minutes",
    image: "/blog/form-backend-setup.webp",
    imageAlt: "HTML Form Backend Setup Guide",
    author: "FormCare Team",
    publishDate: blogDates['html-form-backend-setup-guide'].publishDate,
    readTime: "5 min read",
    excerpt: "Learn how to quickly set up a backend for your HTML forms without writing any server-side code. Perfect for static sites and JAMstack applications. This comprehensive guide covers form services, serverless functions, and best practices with step-by-step instructions and real-world examples."
  },
  'contact-form-security-best-practices': {
    title: "Best Practices for Contact Form Security | FormCare",
    description: "Essential security measures to protect your contact forms from spam, attacks, and data breaches. Complete guide with practical code examples.",
    keywords: "form security, contact form security, spam protection, CSRF protection, form validation, web security, XSS prevention",
    ogTitle: "Best Practices for Contact Form Security",
    image: "/blog/form-security.webp",
    imageAlt: "Contact Form Security Best Practices",
    author: "FormCare Team",
    publishDate: blogDates['contact-form-security-best-practices'].publishDate,
    readTime: "8 min read",
    excerpt: "Essential security measures to protect your contact forms from spam, attacks, and data breaches. Complete guide with practical code examples covering input validation, CSRF protection, spam prevention, file upload security, and monitoring strategies for robust form protection."
  },
  'jamstack-forms-complete-guide': {
    title: "JAMstack Forms: Complete Guide | FormCare",
    description: "Everything you need to know about handling forms in JAMstack applications. From static site generators to form backend services.",
    keywords: "JAMstack forms, static site forms, Gatsby forms, Next.js forms, Hugo forms, Jekyll forms, serverless forms, static site generators",
    ogTitle: "JAMstack Forms: Complete Guide",
    image: "/blog/jamstack-forms.webp",
    imageAlt: "JAMstack Forms Complete Guide",
    author: "FormCare Team",
    publishDate: blogDates['jamstack-forms-complete-guide'].publishDate,
    readTime: "12 min read",
    excerpt: "Everything you need to know about handling forms in JAMstack applications. From static site generators like Next.js, Gatsby, and Hugo to form backend services, serverless functions, and advanced features like file uploads, multi-step forms, and real-time validation."
  },
  'adding-contact-forms-static-sites': {
    title: "Adding Contact Forms to Static Sites: Complete Developer Guide | FormCare",
    description: "Learn how to add functional contact forms to your static websites using modern solutions. Step-by-step guide with code examples.",
    keywords: "static site forms, contact forms, JAMstack, Netlify forms, Vercel forms, static site generators",
    ogTitle: "Adding Contact Forms to Static Sites: Complete Developer Guide",
    image: "/blog/static-site-forms.webp",
    imageAlt: "Adding Contact Forms to Static Sites Guide",
    author: "FormCare Team",
    publishDate: blogDates['adding-contact-forms-static-sites'].publishDate,
    readTime: "12 min read",
    excerpt: "Learn how to add functional contact forms to your static websites using modern solutions. Step-by-step guide with code examples for popular static site generators including FormCare, Netlify Forms, and serverless functions."
  },
  'static-site-forms-solutions-comparison': {
    title: "Static Site Forms: Complete Solutions Comparison 2025 | FormCare",
    description: "Compare the best form solutions for static websites. In-depth analysis of FormCare, Netlify Forms, Formspree, and other popular options.",
    keywords: "form solutions comparison, static site forms, FormCare vs alternatives, form services pricing, form backend comparison",
    ogTitle: "Static Site Forms: Complete Solutions Comparison 2025",
    image: "/blog/static-forms-comparison.webp",
    imageAlt: "Static Site Forms Solutions Comparison",
    author: "FormCare Team",
    publishDate: blogDates['static-site-forms-solutions-comparison'].publishDate,
    readTime: "15 min read",
    excerpt: "Compare the best form solutions for static websites. In-depth analysis of FormCare, Netlify Forms, Formspree, Typeform and other popular options with pricing, features, and use case recommendations."
  },
  'formcare-vs-building-your-own-backend': {
    title: "FormCare vs Building Your Own Backend: Developer's Guide 2025 | FormCare",
    description: "Should you use FormCare or build your own form backend? Complete cost-benefit analysis with code examples and real-world scenarios.",
    keywords: "FormCare vs custom backend, build vs buy, form backend development, cost analysis, development time",
    ogTitle: "FormCare vs Building Your Own Backend: Developer's Guide 2025",
    image: "/blog/formcare-vs-custom-backend.webp",
    imageAlt: "FormCare vs Building Your Own Backend",
    author: "FormCare Team",
    publishDate: blogDates['formcare-vs-building-your-own-backend'].publishDate,
    readTime: "12 min read",
    excerpt: "Should you use FormCare or build your own form backend? Complete cost-benefit analysis with code examples, real-world scenarios, and time investment breakdown to help you make the right choice."
  },
  'preventing-form-spam-bot-attacks': {
    title: "Preventing Form Spam and Bot Attacks: Developer's Defense Guide | FormCare",
    description: "Protect your forms from spam bots and automated attacks with proven defense strategies. Multiple layers of protection guide.",
    keywords: "form spam protection, bot attacks prevention, CAPTCHA, honeypot fields, rate limiting, form security",
    ogTitle: "Preventing Form Spam and Bot Attacks: Developer's Defense Guide",
    image: "/blog/form-spam-protection.webp",
    imageAlt: "Preventing Form Spam and Bot Attacks",
    author: "FormCare Team",
    publishDate: blogDates['preventing-form-spam-bot-attacks'].publishDate,
    readTime: "10 min read",
    excerpt: "Protect your forms from spam bots and automated attacks with these proven defense strategies. Learn to implement multiple layers of protection including honeypot fields, rate limiting, CAPTCHA, and behavioral analysis."
  },
  'contact-form-gdpr-compliance-guide': {
    title: "Contact Form GDPR Compliance: Complete Developer Guide 2025 | FormCare",
    description: "Ensure your contact forms comply with GDPR regulations. Complete guide with practical implementation steps and code examples.",
    keywords: "GDPR compliance, contact form privacy, data protection, privacy regulations, consent management, GDPR forms",
    ogTitle: "Contact Form GDPR Compliance: Complete Developer Guide 2025",
    image: "/blog/gdpr-compliance.webp",
    imageAlt: "Contact Form GDPR Compliance Guide",
    author: "FormCare Team",
    publishDate: blogDates['contact-form-gdpr-compliance-guide'].publishDate,
    readTime: "14 min read",
    excerpt: "Ensure your contact forms comply with GDPR regulations. Complete guide with practical implementation steps, code examples, legal requirements, consent management, and individual rights handling."
  },
  'form-validation-best-practices': {
    title: "Form Validation Best Practices: Complete Developer Guide 2025 | FormCare",
    description: "Master form validation with this comprehensive guide. Learn client-side and server-side validation techniques with code examples.",
    keywords: "form validation, HTML5 validation, JavaScript validation, form UX, accessibility, validation patterns, client-side validation",
    ogTitle: "Form Validation Best Practices: Complete Developer Guide 2025",
    image: "/blog/form-validation.webp",
    imageAlt: "Form Validation Best Practices Guide",
    author: "FormCare Team",
    publishDate: blogDates['form-validation-best-practices'].publishDate,
    readTime: "16 min read",
    excerpt: "Master form validation with this comprehensive guide. Learn client-side and server-side validation techniques, accessibility considerations, UX best practices, and advanced validation patterns with code examples."
  },
  'connecting-flux-ui-to-formcare-guide': {
    title: "How to Connect Flux UI to FormCare: Complete Laravel Integration Guide | FormCare",
    description: "Learn how to integrate Flux UI components with FormCare for beautiful, native form submissions. Step-by-step guide with code examples for Laravel and static sites.",
    keywords: "Flux UI, FormCare integration, Laravel forms, Livewire components, native form submission, contact forms, Tailwind CSS forms",
    ogTitle: "How to Connect Flux UI to FormCare: Complete Laravel Integration Guide",
    image: "/blog/flux-ui-formcare.webp",
    imageAlt: "Flux UI FormCare Integration Guide",
    author: "FormCare Team",
    publishDate: blogDates['connecting-flux-ui-to-formcare-guide'].publishDate,
    readTime: "12 min read",
    excerpt: "Learn how to integrate Flux UI components with FormCare for beautiful, native form submissions. Complete guide covering Laravel Livewire components, static HTML with Flux UI-inspired styling, and best practices for accessible, reliable form handling."
  },
  'nextjs-contact-form-without-firebase': {
    title: "How to Add a Contact Form to a Next.js App (Without Firebase) | FormCare",
    description: "Learn how to add contact forms to your Next.js application without Firebase. Explore modern alternatives that are simpler, more cost-effective, and easier to maintain.",
    keywords: "Next.js contact form, Next.js forms without Firebase, serverless forms, FormCare Next.js, contact form alternatives, Firebase alternatives",
    ogTitle: "How to Add a Contact Form to a Next.js App (Without Firebase)",
    image: "/blog/nextjs-contact-form.webp",
    imageAlt: "Next.js Contact Form Without Firebase",
    author: "FormCare Team",
    publishDate: blogDates['nextjs-contact-form-without-firebase'].publishDate,
    readTime: "8 min read",
    excerpt: "Learn how to add contact forms to your Next.js application without Firebase. Explore modern alternatives like FormCare, Netlify Forms, and API routes that are simpler, more cost-effective, and easier to maintain than complex Firebase setups."
  },
  'react-form-submissions-no-server': {
    title: "The Easiest Way to Handle Form Submissions in React (No Server Setup) | FormCare",
    description: "Learn how to handle form submissions in React applications without setting up any servers. Explore modern serverless solutions that work with any React framework.",
    keywords: "React form submissions, serverless forms, React forms without backend, FormCare React, contact forms React, form handling React",
    ogTitle: "The Easiest Way to Handle Form Submissions in React (No Server Setup)",
    image: "/blog/react-form-submissions.webp",
    imageAlt: "React Form Submissions Without Server Setup",
    author: "FormCare Team",
    publishDate: blogDates['react-form-submissions-no-server'].publishDate,
    readTime: "10 min read",
    excerpt: "Learn how to handle form submissions in React applications without setting up any servers. Comprehensive guide covering FormCare integration, React Hook Form, custom hooks, file uploads, and advanced form features with serverless solutions."
  },
  'aws-lambda-form-endpoint-alternative': {
    title: "Replacing Your AWS Lambda Form Endpoint with a 2-Minute Solution | FormCare",
    description: "Stop managing AWS Lambda functions for simple form handling. Learn how to replace complex serverless setups with modern, developer-friendly alternatives.",
    keywords: "AWS Lambda alternative, serverless forms, FormCare vs Lambda, form endpoint replacement, AWS alternatives, serverless form handling",
    ogTitle: "Replacing Your AWS Lambda Form Endpoint with a 2-Minute Solution",
    image: "/blog/aws-lambda-alternative.webp",
    imageAlt: "AWS Lambda Form Endpoint Alternative",
    author: "FormCare Team",
    publishDate: blogDates['aws-lambda-form-endpoint-alternative'].publishDate,
    readTime: "12 min read",
    excerpt: "Stop managing AWS Lambda functions for simple form handling. Complete migration guide showing how to replace complex AWS setups with FormCare, including cost comparisons, migration steps, and real-world examples."
  },
  'formcare-data-privacy-security': {
    title: "Is Your Form Data Safe with FormCare? Privacy & Security Guide | FormCare",
    description: "Discover how FormCare protects your data with GDPR compliance, EU hosting, encryption, and transparent privacy practices. Learn why your form data is safe with us.",
    keywords: "FormCare security, form data privacy, GDPR compliance, safe form backend, data protection, form security, EU data hosting, privacy policy",
    ogTitle: "Is Your Form Data Safe with FormCare? Our Privacy & Security Commitment",
    image: "/blog/formcare-data-security.webp",
    imageAlt: "FormCare Data Security and Privacy Protection",
    author: "FormCare Team",
    publishDate: blogDates['formcare-data-privacy-security'].publishDate,
    readTime: "8 min read",
    excerpt: "When choosing a form backend service, data security and privacy should be your top priority. Learn exactly how FormCare protects your data with GDPR compliance, EU-only hosting, enterprise-grade encryption, and transparent privacy practices that build trust."
  },
  'webflow-file-uploads-guide': {
    title: "How to Easily Add File Uploads to Your Webflow Forms | Complete Guide | FormCare",
    description: "Step-by-step guide to add file upload functionality to Webflow forms using FormCare. No coding required, works with any Webflow project. Get file uploads working in minutes.",
    keywords: "Webflow file uploads, Webflow forms file upload, FormCare Webflow, Webflow form backend, file upload Webflow forms, Webflow form handling, no-code file uploads",
    ogTitle: "How to Easily Add File Uploads to Your Webflow Forms | Complete Guide",
    image: "/blog/webflow-file-uploads.webp",
    imageAlt: "Webflow Forms with File Upload Integration",
    author: "FormCare Team",
    publishDate: blogDates['webflow-file-uploads-guide'].publishDate,
    readTime: "12 min read",
    excerpt: "Transform your Webflow forms with file upload functionality in minutes. This step-by-step guide shows you how to add secure file uploads to any Webflow form without writing a single line of code, including setup, configuration, and troubleshooting tips."
  },
  'formcare-customer-support-service': {
    title: "FormCare Customer Support: Why Our Service Goes Beyond Just Answering Questions | FormCare",
    description: "Discover how FormCare's customer support service combines human expertise with smart technology to provide exceptional help when you need it most. Real stories and response times.",
    keywords: "FormCare customer support, technical support, form help, customer service, support tickets, help desk, form backend support, developer support",
    ogTitle: "FormCare Customer Support: Why Our Service Goes Beyond Just Answering Questions",
    image: "/blog/formcare-customer-support.webp",
    imageAlt: "FormCare Customer Support Service Excellence",
    author: "FormCare Team",
    publishDate: blogDates['formcare-customer-support-service'].publishDate,
    readTime: "9 min read",
    excerpt: "When you choose FormCare, you're not just getting a form backend service – you're getting a dedicated support team that treats your success as their own. Learn how we're redefining customer support in the developer tools space with real stories, response times, and our support philosophy."
  },
  'custom-notification-email-form-management': {
    title: "Custom Notification Email for Forms: Complete Team Management Guide | FormCare",
    description: "Stop losing form submissions in shared inboxes. Learn how custom notification emails can revolutionize your team's form management workflow and ensure every submission reaches the right person instantly.",
    keywords: "custom notification email, form management, team workflow, form notifications, email routing, form backend, custom email notifications, team collaboration, form organization",
    ogTitle: "Custom Notification Email for Forms: Complete Team Management Guide",
    image: "/blog/custom-notification-email.webp",
    imageAlt: "Custom Notification Email for Form Management",
    author: "FormCare Team",
    publishDate: blogDates['custom-notification-email-form-management'].publishDate,
    readTime: "8 min read",
    excerpt: "Stop losing form submissions in shared inboxes. Learn how custom notification emails can revolutionize your team's form management workflow and ensure every submission reaches the right person instantly."
  },
  'wix-formcare-integration-guide': {
    title: "Wix Form Backend with FormCare: No-Code & Velo Integration Guide | FormCare",
    description: "Use FormCare as your Wix form backend. Connect Wix Forms with Automations, Velo fetch, or HTML embeds—collect submissions, files, and email alerts without maintaining servers.",
    keywords: "Wix form backend, FormCare Wix integration, Wix Forms webhook, Wix Velo form submit, Wix contact form API, Wix Automations webhook, Wix form handler, Wix file upload form, no-code Wix forms",
    ogTitle: "How to Use FormCare as Your Wix Form Backend (2026 Guide)",
    image: "/blog/wix-formcare-integration.webp",
    imageAlt: "Wix website connected to FormCare form backend",
    author: "FormCare Team",
    publishDate: blogDates['wix-formcare-integration-guide'].publishDate,
    readTime: "14 min read",
    excerpt: "Step-by-step guide to use FormCare as your Wix form backend: Wix Automations webhooks for no-code users, Velo backend fetch for custom forms, HTML embeds for file uploads, thank-you redirects, and troubleshooting."
  },
  'chatgpt-cloudflare-landing-page-guide': {
    title: "ChatGPT + Cloudflare Pages + FormCare: Landing Page Tutorial | FormCare",
    description: "Ship a landing page in under an hour: generate HTML with ChatGPT, host free on Cloudflare Pages, and collect contact form leads with FormCare—no backend required.",
    keywords: "ChatGPT landing page, Cloudflare Pages contact form, FormCare tutorial, static site form backend, no-code landing page, HTML form POST, Cloudflare Pages deploy",
    ogTitle: "Build a Landing Page with ChatGPT, Cloudflare Pages, and FormCare",
    image: "/blog/formcare-cloudflare-flow-schema.webp",
    imageAlt: "Flow diagram: Cloudflare Pages static site and FormCare form API",
    author: "FormCare Team",
    publishDate: blogDates['chatgpt-cloudflare-landing-page-guide'].publishDate,
    readTime: "12 min read",
    excerpt: "Step-by-step beginner guide: generate a landing page with ChatGPT, deploy on Cloudflare Pages for free, and connect a working contact form to FormCare—no server, database, or mailto links."
  },
  'formcare-design-update': {
    title: "A Fresh Look for FormCare: UI/UX Design Update | FormCare",
    description: "Explore FormCare's UI/UX redesign: cleaner navigation, a refreshed homepage, simpler endpoint creation, clearer pricing, and a faster path from signup to first submission.",
    keywords: "FormCare design update, UI UX redesign, SaaS redesign, form backend UX, developer tools design, product design update, form endpoint dashboard",
    ogTitle: "A Fresh Look for FormCare: Designing a Faster Form Backend Experience",
    image: "/blog/formcare-design-update.webp",
    imageAlt: "FormCare design update and refreshed product experience",
    author: "FormCare Team",
    publishDate: blogDates['formcare-design-update'].publishDate,
    readTime: "6 min read",
    excerpt: "We refreshed FormCare's interface around one goal: helping teams create an endpoint, connect a form, and see the first submission with less friction. Here's what changed and why it matters."
  },
  'formcare-antispam-form-protection': {
    title: "How FormCare Anti-Spam Protects Your Forms | FormCare",
    description: "Learn how FormCare blocks form spam for you: honeypot fields, name-field link checks, rate limits, and link flooding protection—without CAPTCHA friction for real visitors.",
    keywords: "FormCare anti-spam, form spam protection, honeypot form field, form rate limiting, block spam submissions, HTML form spam filter, form backend spam",
    ogTitle: "How FormCare Anti-Spam Protects Your Forms",
    image: "/blog/formcare-antispam-protection.webp",
    imageAlt: "FormCare anti-spam: spam blocked by a shield while clean submissions pass through",
    author: "FormCare Team",
    publishDate: blogDates['formcare-antispam-form-protection'].publishDate,
    readTime: "7 min read",
    excerpt: "FormCare screens every public submission before it hits your dashboard or inbox. Here is what runs automatically—and how to make it even stronger with one hidden honeypot field."
  },
  'formcare-product-update-simpler-signup': {
    title: "Create a Form, Paste One URL, Get the Email | FormCare",
    description: "FormCare now starts with a named form, a Copy button for the HTML, and email on by default. A submission shows the device it came from. 150 free submissions a month.",
    keywords: "HTML form backend, form signup, contact form email, FormCare update, paste form action, free form backend",
    ogTitle: "Create a form, paste one URL, get the email",
    image: "/blog/formcare-product-update.webp",
    imageAlt: "Illustration of an HTML form, a submit URL, and an inbox with an email",
    author: "FormCare Team",
    publishDate: blogDates['formcare-product-update-simpler-signup'].publishDate,
    readTime: "4 min read",
    excerpt: "Name a form, copy the HTML from the success message, and the next submission arrives by email. Open it and you see the device it came from."
  }
};

export const createBlogPostStructuredData = (slug) => {
  const meta = blogPostMetadata[slug];
  const dates = blogDates[slug];
  if (!meta || !dates) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: meta.ogTitle,
    description: meta.excerpt,
    author: { '@type': 'Organization', name: meta.author },
    publisher: {
      '@type': 'Organization',
      name: 'FormCare',
      logo: { '@type': 'ImageObject', url: 'https://formcare.io/logo.png' },
    },
    datePublished: dates.datePublished,
    dateModified: dates.dateModified,
    image: `https://formcare.io${meta.image}`,
    url: `https://formcare.io/blog/${slug}`,
  };
};

// Export blogPosts array for BlogPage component
export const blogPosts = Object.entries(blogPostMetadata).map(([slug, metadata], index) => ({
  id: index + 1,
  title: metadata.ogTitle,
  excerpt: metadata.excerpt,
  slug: slug,
  author: metadata.author,
  publishDate: metadata.publishDate,
  readTime: metadata.readTime,
  category: getCategoryFromSlug(slug),
  image: metadata.image,
  featured: getFeaturedStatus(slug),
  tags: getTagsFromKeywords(metadata.keywords)
}));

// Helper function to determine category from slug
function getCategoryFromSlug(slug) {
  if (slug.includes('security') || slug.includes('spam') || slug.includes('antispam') || slug.includes('gdpr') || slug.includes('privacy')) return 'Security';
  if (slug.includes('jamstack')) return 'JAMstack';
  if (slug.includes('comparison')) return 'Comparison';
  if (slug.includes('validation')) return 'Best Practices';
  if (slug.includes('nextjs')) return 'Next.js';
  if (slug.includes('react')) return 'React';
  if (slug.includes('aws') || slug.includes('lambda')) return 'AWS';
  if (slug.includes('webflow')) return 'Webflow';
  if (slug.includes('wix')) return 'Wix';
  if (slug.includes('cloudflare') || slug.includes('chatgpt')) return 'Tutorial';
  if (slug.includes('formcare-data')) return 'Trust & Safety';
  if (slug.includes('customer-support') || slug.includes('support-service')) return 'Customer Success';
  if (slug.includes('custom-notification') || slug.includes('team-management')) return 'Team Management';
  if (slug.includes('design') || slug.includes('ui') || slug.includes('ux') || slug.includes('product-update')) return 'Product Updates';
  if (slug.includes('tutorial') || slug.includes('guide') || slug.includes('setup')) return 'Tutorial';
  return 'Tutorial';
}

// Helper function to determine featured status
function getFeaturedStatus(slug) {
  const featuredSlugs = [
    'html-form-backend-setup-guide',
    'contact-form-security-best-practices',
    'jamstack-forms-complete-guide',
    'static-site-forms-solutions-comparison',
    'form-validation-best-practices'
  ];
  return featuredSlugs.includes(slug);
}

// Helper function to convert keywords to tags
function getTagsFromKeywords(keywords) {
  return keywords.split(', ').slice(0, 5); // Take first 5 keywords as tags
} 